import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSiteStore } from '../stores/siteStore';
import { riskAPI } from '../utils/api';
import RiskGaugeChart from '../components/risk/RiskGaugeChart';
import ForecastLineChart from '../components/risk/ForecastLineChart';
import AnomalyTimeline from '../components/risk/AnomalyTimeline';
import RiskHeatmap from '../components/risk/RiskHeatmap';
import RiskScoreHistory from '../components/risk/RiskScoreHistory';
import DataConfidenceBadge from '../components/risk/DataConfidenceBadge';
import RiskBreakdownRadar from '../components/risk/RiskBreakdownRadar';
import ZoneComparison from '../components/risk/ZoneComparison';
import TrendSparkline from '../components/risk/TrendSparkline';
import {
  ShieldExclamationIcon,
  ArrowTrendingUpIcon,
  ExclamationTriangleIcon,
  ChartBarSquareIcon,
  ArrowPathIcon,
  XMarkIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';

export default function RiskAnalyticsPage() {
  const { selectedSite } = useSiteStore();
  const siteId = selectedSite?.id;
  const queryClient = useQueryClient();
  const [selectedZone, setSelectedZone] = useState(null);

  // Dashboard data
  const { data: dashboard, isLoading, isFetching } = useQuery({
    queryKey: ['risk-dashboard', siteId],
    queryFn: () => riskAPI.getDashboard({ siteId }).then(r => r.data),
    enabled: !!siteId,
    refetchInterval: 5 * 60 * 1000,
  });

  // Anomalies
  const { data: anomalyData } = useQuery({
    queryKey: ['risk-anomalies', siteId],
    queryFn: () => riskAPI.getAnomalies({ siteId, status: 'active' }).then(r => r.data),
    enabled: !!siteId,
    refetchInterval: 60 * 1000,
  });

  // Forecasts
  const { data: forecasts } = useQuery({
    queryKey: ['risk-forecasts', siteId],
    queryFn: () => riskAPI.getAllForecasts({ siteId }).then(r => r.data),
    enabled: !!siteId,
  });

  // Risk score history
  const { data: historyData } = useQuery({
    queryKey: ['risk-history', siteId],
    queryFn: () => riskAPI.getScoresHistory({ siteId, days: 30 }).then(r => r.data),
    enabled: !!siteId,
    staleTime: 10 * 60 * 1000, // 10 minute cache
  });

  // Acknowledge mutation
  const acknowledgeMutation = useMutation({
    mutationFn: (id) => riskAPI.acknowledgeAnomaly(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['risk-anomalies'] });
      queryClient.invalidateQueries({ queryKey: ['risk-dashboard'] });
    },
  });

  // Recalculate mutation
  const recalcMutation = useMutation({
    mutationFn: () => riskAPI.calculateScores({ siteId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['risk-dashboard'] });
    },
  });

  if (!siteId) {
    return (
      <div className="p-8 text-center">
        <ShieldExclamationIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-slate-700">Risk Analytics</h2>
        <p className="text-slate-500 mt-2">Select a site to view risk analytics</p>
      </div>
    );
  }

  const siteScore = dashboard?.riskOverview?.site;
  const zoneScores = dashboard?.riskOverview?.zones || [];

  // Normalize anomaly data from different sources
  const anomalies = (() => {
    if (Array.isArray(anomalyData?.anomalies)) return anomalyData.anomalies;
    if (Array.isArray(dashboard?.anomalies?.active)) return dashboard.anomalies.active;
    if (Array.isArray(anomalyData)) return anomalyData;
    return [];
  })();

  const anomalyCount = anomalies.length > 0 ? anomalies.length :
                       (anomalyData?.total || dashboard?.anomalies?.count || 0);
  const forecastOutlook = dashboard?.forecastOutlook || 'stable';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Risk Analytics</h1>
          <p className="text-sm text-slate-500 mt-1">{selectedSite?.name} — Predictive risk assessment</p>
        </div>
        <button
          onClick={() => recalcMutation.mutate()}
          disabled={recalcMutation.isPending}
          className="btn btn-secondary flex items-center gap-2"
        >
          <ArrowPathIcon className={`w-4 h-4 ${recalcMutation.isPending ? 'animate-spin' : ''}`} />
          Recalculate
        </button>
      </div>

      {/* Overview Cards */}
      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 transition-opacity duration-300 ${isFetching && !isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
        {/* Risk Score Gauge */}
        <div className="glass p-4 flex flex-col items-center">
          <div className="flex items-center justify-between w-full mb-2">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Site Risk Score</h3>
            {historyData?.summary && (
              <DataConfidenceBadge
                confidence={historyData.summary.confidence || 0}
                agesDays={historyData.summary.agesDays || 0}
                dataFreshness={historyData.summary.dataFreshness || 'stale'}
              />
            )}
          </div>
          {isLoading ? (
            <div className="h-24 flex items-center justify-center">
              <div className="animate-pulse w-20 h-20 rounded-full bg-slate-200" />
            </div>
          ) : (
            <RiskGaugeChart
              score={siteScore?.overallScore || 0}
              riskLevel={siteScore?.riskLevel || 'LOW'}
              trend={siteScore?.trend || 'stable'}
              size={140}
            />
          )}
        </div>

        {/* Trend Indicator */}
        <div className="glass p-4">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Trend</h3>
          <div className="flex items-center gap-3 mt-4">
            {siteScore?.trend === 'improving' ? (
              <ArrowTrendingDownIcon className="w-8 h-8 text-green-500" />
            ) : siteScore?.trend === 'worsening' ? (
              <ArrowTrendingUpIcon className="w-8 h-8 text-red-500" />
            ) : (
              <ArrowTrendingUpIcon className="w-8 h-8 text-slate-400" />
            )}
            <div>
              <p className="text-lg font-bold text-slate-700 capitalize">{siteScore?.trend || 'Calculating...'}</p>
              <p className="text-xs text-slate-500">Safety trajectory</p>
            </div>
          </div>
        </div>

        {/* Anomaly Count */}
        <div className="glass p-4">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Active Anomalies</h3>
          <div className="flex items-center gap-3 mt-4">
            <ExclamationTriangleIcon className={`w-8 h-8 ${anomalyCount > 0 ? 'text-amber-500' : 'text-emerald-400'}`} />
            <div>
              <p className="text-3xl font-black text-slate-800">{anomalyCount}</p>
              <p className="text-xs text-slate-500">Detected anomalies</p>
            </div>
          </div>
        </div>

        {/* Forecast Outlook */}
        <div className="glass p-4">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Forecast Outlook</h3>
          <div className="flex items-center gap-3 mt-4">
            <ChartBarSquareIcon className="w-8 h-8 text-slate-400" />
            <div>
              <p className={`text-lg font-bold capitalize ${
                forecastOutlook === 'positive' ? 'text-emerald-600' :
                forecastOutlook === 'concerning' ? 'text-amber-600' : 'text-slate-700'
              }`}>
                {forecastOutlook}
              </p>
              <p className="text-xs text-slate-500">7-day prediction</p>
            </div>
          </div>
        </div>
      </div>

      {/* Risk Heatmap & Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RiskHeatmap zones={zoneScores} onZoneClick={setSelectedZone} />

        {/* Risk Breakdown Radar Chart */}
        <div className="glass p-6 flex flex-col items-center">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 w-full">Risk Factor Breakdown</h3>
          {isLoading ? (
            <div className="flex items-center justify-center w-full h-80">
              <div className="animate-pulse">
                <div className="w-56 h-56 rounded-full bg-slate-200" />
              </div>
            </div>
          ) : siteScore ? (
            <RiskBreakdownRadar siteScore={siteScore} size={280} />
          ) : (
            <div className="text-center py-6 w-full">
              <p className="text-sm text-slate-500 mb-3">Risk data is being calculated...</p>
              <button
                onClick={() => recalcMutation.mutate()}
                disabled={recalcMutation.isPending || isLoading}
                className="text-xs px-3 py-1 bg-slate-200 hover:bg-slate-300 rounded transition-colors"
              >
                Recalculate Now
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Zone Comparison & Top Zones */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ZoneComparison zones={zoneScores} limit={5} />

        {/* Zone Drill-Down Details */}
        {zoneScores.length === 0 && (
          <div className="glass p-6 text-center">
            <p className="text-sm text-slate-400">No zone data available</p>
          </div>
        )}
      </div>

      {/* Risk Factor Trends - Sparklines */}
      {historyData?.history && historyData.history.length > 1 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="glass p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-slate-600 uppercase">Alert Frequency</h4>
              <span className="text-sm font-bold text-slate-800">{Math.round(siteScore?.alertFreqScore || 0)}</span>
            </div>
            <TrendSparkline
              data={historyData.history.map(h => h.siteScore?.alertFreqScore || 0)}
              color="bg-blue-500"
              height={24}
            />
          </div>

          <div className="glass p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-slate-600 uppercase">Severity</h4>
              <span className="text-sm font-bold text-slate-800">{Math.round(siteScore?.severityScore || 0)}</span>
            </div>
            <TrendSparkline
              data={historyData.history.map(h => h.siteScore?.severityScore || 0)}
              color="bg-orange-500"
              height={24}
            />
          </div>

          <div className="glass p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-slate-600 uppercase">Compliance</h4>
              <span className="text-sm font-bold text-slate-800">{Math.round(siteScore?.complianceScore || 0)}</span>
            </div>
            <TrendSparkline
              data={historyData.history.map(h => h.siteScore?.complianceScore || 0)}
              color="bg-amber-500"
              height={24}
            />
          </div>

          <div className="glass p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-slate-600 uppercase">Incidents</h4>
              <span className="text-sm font-bold text-slate-800">{Math.round(siteScore?.incidentScore || 0)}</span>
            </div>
            <TrendSparkline
              data={historyData.history.map(h => h.siteScore?.incidentScore || 0)}
              color="bg-red-500"
              height={24}
            />
          </div>
        </div>
      )}

      {/* Forecast Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ForecastLineChart
          data={forecasts?.compliance}
          title="Compliance Forecast"
          unit="%"
        />
        <ForecastLineChart
          data={forecasts?.alertRate}
          title="Alert Rate Forecast"
          unit=" /1000hrs"
        />
      </div>

      {/* Risk Score History */}
      {historyData && (
        <RiskScoreHistory
          history={historyData.history || []}
          summary={historyData.summary || {}}
        />
      )}

      {/* Zone Detail Panel */}
      {selectedZone && (
        <div className="glass p-6 animate-slide-in">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <MapPinIcon className="w-5 h-5 text-slate-600" />
                <h3 className="text-lg font-bold text-slate-800">{selectedZone.name}</h3>
              </div>
              <p className="text-sm text-slate-500">Zone ID: {selectedZone.id}</p>
            </div>
            <button
              onClick={() => setSelectedZone(null)}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-5 h-5 text-slate-600" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1">Risk Level</p>
              <p className={`text-lg font-bold capitalize ${
                selectedZone.riskLevel === 'CRITICAL' ? 'text-red-600' :
                selectedZone.riskLevel === 'HIGH' ? 'text-orange-600' :
                selectedZone.riskLevel === 'MEDIUM' ? 'text-amber-600' :
                'text-emerald-600'
              }`}>
                {selectedZone.riskLevel || 'N/A'}
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1">Overall Score</p>
              <p className="text-lg font-bold text-slate-800">{Math.round(selectedZone.overallScore || 0)}/100</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1">Alert Frequency</p>
              <p className="text-lg font-bold text-slate-800">{Math.round(selectedZone.alertFreqScore || 0)}/100</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1">Severity</p>
              <p className="text-lg font-bold text-slate-800">{Math.round(selectedZone.severityScore || 0)}/100</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1">Compliance</p>
              <p className="text-lg font-bold text-slate-800">{Math.round(selectedZone.complianceScore || 0)}/100</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1">Incidents</p>
              <p className="text-lg font-bold text-slate-800">{Math.round(selectedZone.incidentScore || 0)}/100</p>
            </div>
          </div>
        </div>
      )}

      {/* Anomaly Timeline */}
      <AnomalyTimeline
        anomalies={anomalies}
        onAcknowledge={(id) => acknowledgeMutation.mutate(id)}
      />
    </div>
  );
}
